import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
  //Upload as UploadIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  MergeType as MergeTypeIcon,
  Add as AddIcon,
  //Edit as EditIcon
} from '@mui/icons-material';
import scheduleOfActivitiesService from '../../services/scheduleOfActivities.service';

const stripFootnoteNumber = (text) => {
  if (!text) return '';
  return text.replace(/^\s*\d+[\.\)]\s*/, '').trim();
};

const ExcelLikeSOA = ({ data, protocolId, onDataChange, readOnly = false }) => {
  const [excelData, setExcelData] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [dataModified, setDataModified] = useState(false);
  const [selectedCols, setSelectedCols] = useState([]);
  const [addColOpen, setAddColOpen] = useState(false);
  const [newColName, setNewColName] = useState('');
  const [addRowOpen, setAddRowOpen] = useState(false);
  const [newRowType, setNewRowType] = useState('activity');
  const [newRowName, setNewRowName] = useState('');
  const [insertAfterRow, setInsertAfterRow] = useState(-1);
  const [insertAfterCol, setInsertAfterCol] = useState(-1);
  const [rowPosition, setRowPosition] = useState('end'); // 'beginning', 'after', 'before', 'end'
  const [colPosition, setColPosition] = useState('end'); // 'beginning', 'after', 'before', 'end'
  const [editingColumn, setEditingColumn] = useState(null); // For editing column names
  const [editColumnValue, setEditColumnValue] = useState('');
  const [editColumnDialogOpen, setEditColumnDialogOpen] = useState(false);
  const [editingColumnIndex, setEditingColumnIndex] = useState(null);
  const [editColumnPeriod, setEditColumnPeriod] = useState('');
  const [editColumnDay, setEditColumnDay] = useState('');
  const [keynotesDialogOpen, setKeynotesDialogOpen] = useState(false);
  const [editingKeynotesRowIndex, setEditingKeynotesRowIndex] = useState(null);
  const [editingKeynotesActivityName, setEditingKeynotesActivityName] = useState('');
  const [keynotesEditValue, setKeynotesEditValue] = useState('');
  // Initialize Excel-like data structure when data changes
  useEffect(() => {
    if (data) {
      const header = ['Study procedures and visits', ...data.visits.map(visit => `${visit.period}\n${visit.day}`), 'Key Notes'];
      const footnotes = (data.footnotes || []).map(stripFootnoteNumber);
      let rows = [];
      
      data.sections.forEach(section => {
        // Add section row
        const sectionRow = {
          id: `section-${section.title}`,
          type: 'section',
          data: [section.title, ...Array(header.length - 1).fill('')]
        };
        rows.push(sectionRow);
        
        // Add activity rows
        section.activities.forEach((activity, activityIndex) => {
          // Get complete footnote text if power exists
          const keynotesValue = (activity.power && footnotes[activity.power - 1]) ? stripFootnoteNumber(footnotes[activity.power - 1]) : '';
          
          const activityRow = {
            id: `activity-${section.title}-${activityIndex}`,
            type: 'activity',
            section: section.title,
            data: [
              activity.name,
              ...data.visits.map((_, idx) => activity.visits.includes(idx) ? 'X' : ''),
              keynotesValue // Add complete footnote text
            ]
          };
          rows.push(activityRow);
        });
      });
      
      setExcelData({ header, rows });
      setDataModified(false); // Reset modified state when loading new data
    }
  }, [data]);

  // Function to refresh data from database (auto or manual refresh)
  const refreshData = async (isManual = false) => {
    try {
      if (isManual) {
        console.log('ExcelLikeSOA: Manual refresh requested - reloading from database');
      } else {
        console.log('ExcelLikeSOA: Auto-refresh on page load - reloading from database');
      }
      
      const response = await scheduleOfActivitiesService.getScheduleOfActivities(protocolId);
      if (response && response.success && response.data) {
        // Force reinitialize with fresh data from database
        const header = ['Study procedures and visits', ...response.data.visits.map(visit => `${visit.period}\n${visit.day}`), 'Key Notes'];
        const footnotes = (response.data.footnotes || []).map(stripFootnoteNumber);
        let rows = [];
        
        response.data.sections.forEach(section => {
          // Add section row
          const sectionRow = {
            id: `section-${section.title}`,
            type: 'section',
            data: [section.title, ...Array(header.length - 1).fill('')]
          };
          rows.push(sectionRow);
          
          // Add activity rows
          section.activities.forEach((activity, activityIndex) => {
            // Get complete footnote text if power exists
            const keynotesValue = (activity.power && footnotes[activity.power - 1]) ? footnotes[activity.power - 1] : '';
            
            const activityRow = {
              id: `activity-${section.title}-${activityIndex}`,
              type: 'activity',
              section: section.title,
              data: [
                activity.name,
                ...response.data.visits.map((_, idx) => activity.visits.includes(idx) ? 'X' : ''),
                keynotesValue // Add complete footnote text
              ]
            };
            rows.push(activityRow);
          });
        });
        
        setExcelData({ header, rows });
        setDataModified(false); // Reset modified state after refresh
        
        // Update the parent component with fresh data
        if (onDataChange) {
          await onDataChange(response.data);
        }
        
        console.log('ExcelLikeSOA: Data refreshed from database');
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  const handleCellClick = (rowIndex, colIndex) => {
    if (readOnly) return;
    console.log('Cell clicked:', { rowIndex, colIndex, currentEditingCell: editingCell, isEditing });
    
    // Don't allow editing if already editing this cell
    if (editingCell && editingCell.rowIndex === rowIndex && editingCell.colIndex === colIndex) {
      console.log('Already editing this cell, ignoring click');
      return;
    }
    
    const isKeynotesColumn = colIndex === excelData.header.length - 1;
    
    if (excelData.rows[rowIndex].type === 'section' && colIndex === 0) {
      // Allow editing section titles
      setEditingCell({ rowIndex, colIndex });
      setEditValue(excelData.rows[rowIndex].data[colIndex]);
      setIsEditing(true);
      console.log('Started editing section title');
    } else if (excelData.rows[rowIndex].type === 'activity') {
      if (colIndex === 0) {
        // Allow editing activity names
        setEditingCell({ rowIndex, colIndex });
        setEditValue(excelData.rows[rowIndex].data[colIndex]);
        setIsEditing(true);
        console.log('Started editing activity name');
      } else if (isKeynotesColumn) {
        // Open dialog for editing Keynotes column
        setEditingKeynotesRowIndex(rowIndex);
        setEditingKeynotesActivityName(excelData.rows[rowIndex].data[0] || '');
        setKeynotesEditValue(excelData.rows[rowIndex].data[colIndex] || '');
        setKeynotesDialogOpen(true);
        console.log('Opening keynotes dialog');
      } else if (colIndex > 0 && colIndex < excelData.header.length - 1) {
        // Toggle X for visit columns (exclude first column and last Keynotes column)
        const newData = { ...excelData };
        const currentValue = newData.rows[rowIndex].data[colIndex];
        newData.rows[rowIndex].data[colIndex] = currentValue === 'X' ? '' : 'X';
        setExcelData(newData);
        setDataModified(true);
        console.log('Toggled visit column');
      }
    }
  };

  const handleEditSave = () => {
    if (editingCell) {
      const newData = { ...excelData };
      newData.rows[editingCell.rowIndex].data[editingCell.colIndex] = editValue;
      setExcelData(newData);
      setDataModified(true);
      
      // Reset editing state
      setEditingCell(null);
      setEditValue('');
      setIsEditing(false);
      
      console.log('Edit saved, editing state reset:', { editingCell: null, isEditing: false });
    }
  };

  const handleKeynotesDialogSave = () => {
    if (editingKeynotesRowIndex !== null) {
      const newData = { ...excelData };
      const keynotesColumnIndex = excelData.header.length - 1;
      newData.rows[editingKeynotesRowIndex].data[keynotesColumnIndex] = keynotesEditValue;
      setExcelData(newData);
      setDataModified(true);
      
      console.log('Keynotes saved, will update footnotes on table save');
    }
    handleKeynotesDialogClose();
  };

  const handleKeynotesDialogClose = () => {
    setKeynotesDialogOpen(false);
    setEditingKeynotesRowIndex(null);
    setEditingKeynotesActivityName('');
    setKeynotesEditValue('');
  };

  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValue('');
    setIsEditing(false);
    console.log('Edit cancelled, editing state reset:', { editingCell: null, isEditing: false });
  };

  const handleColumnEdit = (colIndex) => {
    if (colIndex === 0) return; // Don't allow editing the first column (row labels)
    setEditingColumnIndex(colIndex);
    
    // Parse the current column header (format: "Period\nDay")
    const currentHeader = excelData.header[colIndex];
    const [period, day] = currentHeader.split('\n');
    
    setEditColumnPeriod(period || '');
    setEditColumnDay(day || '');
    setEditColumnDialogOpen(true);
  };

  const handleColumnSave = () => {
    if (editingColumnIndex !== null && editColumnPeriod.trim()) {
      const newData = { ...excelData };
      // Combine period and day with newline separator
      const newHeaderValue = `${editColumnPeriod.trim()}\n${editColumnDay.trim()}`;
      newData.header[editingColumnIndex] = newHeaderValue;
      setExcelData(newData);
      setDataModified(true);
      console.log('Column name saved:', newHeaderValue);
    }
    handleColumnDialogClose();
  };

  const handleColumnCancel = () => {
    handleColumnDialogClose();
    console.log('Column edit cancelled');
  };

  const handleColumnDialogClose = () => {
    setEditColumnDialogOpen(false);
    setEditingColumnIndex(null);
    setEditColumnValue('');
    setEditColumnPeriod('');
    setEditColumnDay('');
  };

  const handleDeleteColumn = (colIndex) => {
    if (colIndex === 0) return; // Don't delete the first column (row labels)
    const newHeader = excelData.header.filter((_, idx) => idx !== colIndex);
    const newRows = excelData.rows.map(row => ({
      ...row,
      data: row.data.filter((_, idx) => idx !== colIndex)
    }));
    setExcelData({ header: newHeader, rows: newRows });
    setDataModified(true);
  };

  const handleHeaderClick = (colIdx) => {
    if (colIdx === 0) return;
    setSelectedCols((prev) =>
      prev.includes(colIdx)
        ? prev.filter(idx => idx !== colIdx)
        : [...prev, colIdx]
    );
  };

  const handleMergeColumns = () => {
    if (selectedCols.length < 2) return;
    const [start, end] = [Math.min(...selectedCols), Math.max(...selectedCols)];
    const newHeader = [
      ...excelData.header.slice(0, start),
      excelData.header.slice(start, end + 1).join(' / '),
      ...excelData.header.slice(end + 1)
    ];
    const newRows = excelData.rows.map(row => {
      const mergedCells = row.data.slice(start, end + 1);
      let mergedValue;
      if (row.type === 'activity') {
        // If any cell in the merged range is 'X', merged cell is 'X', else ''
        mergedValue = mergedCells.includes('X') ? 'X' : '';
      } else {
        // For section rows, just join as before
        mergedValue = mergedCells.join(' / ');
      }
      return {
        ...row,
        data: [
          ...row.data.slice(0, start),
          mergedValue,
          ...row.data.slice(end + 1)
        ]
      };
    });
    setExcelData({ header: newHeader, rows: newRows });
    setSelectedCols([]);
    setDataModified(true);
  };

  const handleAddRow = () => {
    setNewRowType('activity');
    setNewRowName('');
    setInsertAfterRow(-1);
    setRowPosition('end');
    setAddRowOpen(true);
  };

  const handleAddColumn = () => {
    setNewColName('');
    setInsertAfterCol(-1);
    setColPosition('end');
    setAddColOpen(true);
  };

  const handleAddColumnConfirm = () => {
    if (!newColName.trim()) return;
    
    let insertIndex;
    switch (colPosition) {
      case 'beginning':
        insertIndex = 1; // After the first column (Study procedures and visits)
        break;
      case 'after':
        insertIndex = insertAfterCol + 1;
        break;
      case 'before':
        insertIndex = insertAfterCol;
        break;
      case 'end':
      default:
        insertIndex = excelData.header.length;
        break;
    }
    
    const newHeader = [
      ...excelData.header.slice(0, insertIndex),
      newColName,
      ...excelData.header.slice(insertIndex)
    ];
    
    const newRows = excelData.rows.map(row => ({
      ...row,
      data: [
        ...row.data.slice(0, insertIndex),
        '',
        ...row.data.slice(insertIndex)
      ]
    }));
    
    setExcelData({ header: newHeader, rows: newRows });
    setAddColOpen(false);
    setNewColName('');
    setInsertAfterCol(-1);
    setColPosition('end');
    setDataModified(true);
  };

  const handleAddRowConfirm = () => {
    if (!newRowName.trim()) return;
    
    let insertIndex;
    switch (rowPosition) {
      case 'beginning':
        insertIndex = 0;
        break;
      case 'after':
        insertIndex = insertAfterRow + 1;
        break;
      case 'before':
        insertIndex = insertAfterRow;
        break;
      case 'end':
      default:
        insertIndex = excelData.rows.length;
        break;
    }
    
    if (newRowType === 'section') {
      // Add new section
      const newSection = {
        id: `section-${newRowName}-${Date.now()}`,
        type: 'section',
        data: [newRowName, ...Array(excelData.header.length - 1).fill('')]
      };
      
      const newRows = [
        ...excelData.rows.slice(0, insertIndex),
        newSection,
        ...excelData.rows.slice(insertIndex)
      ];
      
      setExcelData(prev => ({ ...prev, rows: newRows }));
    } else {
      // Add new activity - find the section it belongs to
      let targetSection = null;
      let sectionIndex = -1;
      
      // Find the section to add the activity under
      for (let i = insertIndex - 1; i >= 0; i--) {
        if (excelData.rows[i].type === 'section') {
          targetSection = excelData.rows[i];
          sectionIndex = i;
          break;
        }
      }
      
      // If no section found before insert point, find the next section
      if (!targetSection) {
        for (let i = insertIndex; i < excelData.rows.length; i++) {
          if (excelData.rows[i].type === 'section') {
            targetSection = excelData.rows[i];
            sectionIndex = i;
            break;
          }
        }
      }
      
      // If still no section found, create a default one
      if (!targetSection) {
        const newSection = {
          id: `section-New Section-${Date.now()}`,
          type: 'section',
          data: ['New Section', ...Array(excelData.header.length - 1).fill('')]
        };
        const newActivity = {
          id: `activity-New Section-${Date.now()}`,
          type: 'activity',
          section: 'New Section',
          data: [newRowName, ...Array(excelData.header.length - 1).fill('')]
        };
        
        const newRows = [
          ...excelData.rows.slice(0, insertIndex),
          newSection,
          newActivity,
          ...excelData.rows.slice(insertIndex)
        ];
        
        setExcelData(prev => ({ ...prev, rows: newRows }));
      } else {
        // Add activity under the found section
        // Data array: [activity name, ...visit columns, key column]
        const newActivity = {
          id: `activity-${targetSection.data[0]}-${Date.now()}`,
          type: 'activity',
          section: targetSection.data[0],
          data: [newRowName, ...Array(excelData.header.length - 2).fill(''), ''] // -2 because we have first column and Key column
        };
        
        // Insert after the section or at the specified position
        const insertPos = insertIndex > sectionIndex ? insertIndex : sectionIndex + 1;
        const newRows = [
          ...excelData.rows.slice(0, insertPos),
          newActivity,
          ...excelData.rows.slice(insertPos)
        ];
        
        setExcelData(prev => ({ ...prev, rows: newRows }));
      }
    }
    
    setAddRowOpen(false);
    setNewRowName('');
    setNewRowType('activity');
    setInsertAfterRow(-1);
    setRowPosition('end');
    setDataModified(true);
  };

  const transformToBackendFormat = () => {
    const header = excelData.header;
    // Filter out "Keynotes" column when extracting visits
    const visitHeaders = header.filter(h => h !== 'Key Notes' && h !== 'Keynotes' && h !== 'Study procedures and visits');
    const visits = visitHeaders.map((h, index) => {
      const [period, day] = h.split('\n');
      return { 
        period: period || `Visit ${index + 1}`, 
        day: day || `Day ${index + 1}` 
      };
    });

    let sections = [];
    let currentSection = null;
    const keynotesColumnIndex = header.length - 1; // Last column is "Keynotes"
    let footnotes = [...(data.footnotes || [])].map(stripFootnoteNumber);

    excelData.rows.forEach(row => {
      if (row.type === 'section') {
        if (currentSection) sections.push(currentSection);
        currentSection = { title: row.data[0], activities: [] };
      } else if (row.type === 'activity' && currentSection) {
        // Extract footnote number by matching the text with footnotes array
        const keynotesValue = stripFootnoteNumber((row.data[keynotesColumnIndex] || '').trim());
        let power = undefined;
        if (keynotesValue) {
          let footnoteIndex = footnotes.findIndex(fn => fn === keynotesValue);
          if (footnoteIndex === -1) {
            footnotes.push(keynotesValue);
            footnoteIndex = footnotes.length - 1;
          }
          power = footnoteIndex + 1; // Footnotes are 1-indexed
        }
        
        const activity = {
          name: row.data[0],
          visits: row.data.slice(1, keynotesColumnIndex).map((cell, idx) => cell === 'X' ? idx : null).filter(idx => idx !== null),
          power: power || undefined
        };
        currentSection.activities.push(activity);
      }
    });

    if (currentSection) sections.push(currentSection);

    return { visits, sections, footnotes};
  };

  const validateData = (visits, sections) => {
    // Check for empty visit fields
    for (let i = 0; i < visits.length; i++) {
      if (!visits[i].period || !visits[i].day) {
        throw new Error(`Visit ${i + 1} is missing period or day information. Please ensure all columns have both period and day values.`);
      }
    }
    
    // Check for empty sections
    if (!sections || sections.length === 0) {
      throw new Error('No sections found. Please add at least one section with activities.');
    }
    
    return true;
  };

  const handleSave = async () => {
    if (readOnly) return;
    setSaveLoading(true);
    setSaveError('');
    try {
      // Validate protocolId before saving
      if (!protocolId) {
        throw new Error('Protocol ID is required. Please ensure you are working with a valid protocol.');
      }

      const { visits, sections, footnotes } = transformToBackendFormat();
      
      // Validate data before saving
      validateData(visits, sections);
      
      const updatedData = {
        protocolId,
        visits,
        sections,
        footnotes: footnotes || [],
        summary: data.summary || ''
      };
      
      // Save to database
      await scheduleOfActivitiesService.saveScheduleOfActivities(updatedData);
      
      // Notify parent component of the change
      if (onDataChange) {
        await onDataChange(updatedData);
      }
      
      setSaveSuccess(true);
      setDataModified(false); // Reset modified state after saving
      
      // Show success message for a few seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
      
    } catch (err) {
      setSaveError(err.message || 'Failed to save table');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      await scheduleOfActivitiesService.exportSoAToExcel(protocolId);
    } catch (error) {
      setSaveError('Failed to export to Excel');
    }
  };


  const getCellStyle = (rowIndex, colIndex, value) => {
    const isKeynotesCol = excelData.header && colIndex === excelData.header.length - 1;
    const baseStyle = {
      border: '1px solid #ddd',
      padding: '8px',
      cursor: 'pointer',
      minWidth: colIndex === 0 ? '200px' : isKeynotesCol ? '200px' : '80px',
      maxWidth: isKeynotesCol ? '400px' : undefined,
      whiteSpace: isKeynotesCol ? 'normal' : undefined,
      textAlign: colIndex === 0 || isKeynotesCol ? 'left' : 'center',
      position: 'relative'
    };

    if (excelData.rows[rowIndex]?.type === 'section') {
      return {
        ...baseStyle,
        backgroundColor: '#f0f0f0',
        fontWeight: 'bold',
        textAlign: 'left'
      };
    }

    if (colIndex === 0) {
      return {
        ...baseStyle,
        textAlign: 'left',
        fontWeight: '500'
      };
    }

    if (value === 'X') {
      return {
        ...baseStyle,
        backgroundColor: '#e8f5e8',
        fontWeight: 'bold',
        color: '#2e7d32'
      };
    }

    return baseStyle;
  };

  if (!excelData.header) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Loading Schedule of Activities...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Schedule of Activities (Excel View)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {!readOnly && (
          <>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleAddRow}
            startIcon={<AddIcon />}
          >
            Add Row
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleAddColumn}
            startIcon={<AddIcon />}
          >
            Add Column
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleSave}
            disabled={saveLoading}
            startIcon={<SaveIcon />}
          >
            {saveLoading ? 'Saving...' : dataModified ? 'Save Changes*' : 'Save Changes'}
          </Button>
          </>
          )}
          <Button
            variant="outlined"
            color="success"
            onClick={handleExportExcel}
            startIcon={<DownloadIcon />}
          >
            Export to Excel
          </Button>
           {!readOnly && (
           <Button
             variant="outlined"
             color="info"
             onClick={() => refreshData(true)}
             startIcon={<RefreshIcon />}
           >
             Refresh Data
           </Button>
           )}
        </Box>
      </Box>

      <Paper sx={{ overflow: 'auto', overflowX: 'auto', maxHeight: '600px', width: '100%' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {excelData.header.map((col, colIdx) => (
                  <TableCell
                    key={colIdx}
                    onClick={() => handleHeaderClick(colIdx)}
                    style={{
                      background: selectedCols.includes(colIdx) ? '#ffe066' : undefined,
                      cursor: colIdx > 0 ? 'pointer' : undefined,
                      width: 'auto',
                      minWidth: colIdx === excelData.header.length - 1 ? '200px' : '120px',
                      maxWidth: colIdx === excelData.header.length - 1 ? '400px' : '200px',
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      width: '100%',
                      minHeight: '32px'
                    }}>
                      <span style={{ 
                        flex: 1, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginRight: '8px'
                      }}>
                        {col}
                      </span>
                      {colIdx > 0 && (
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 0.5,
                          opacity: 0.7,
                          '&:hover': { opacity: 1 }
                        }}>
                          <Tooltip title="Edit Column Name">
                            <IconButton 
                              size="small" 
                              onClick={e => { 
                                e.stopPropagation(); 
                                handleColumnEdit(colIdx); 
                              }}
                              sx={{
                                padding: '4px',
                                '&:hover': { 
                                  backgroundColor: '#e3f2fd',
                                  color: '#1976d2'
                                }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Column">
                            <IconButton 
                              size="small" 
                              onClick={e => { 
                                e.stopPropagation(); 
                                handleDeleteColumn(colIdx); 
                              }}
                              sx={{
                                padding: '4px',
                                '&:hover': { 
                                  backgroundColor: '#ffebee',
                                  color: '#d32f2f'
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                ))}
                {selectedCols.length > 1 && (
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<MergeTypeIcon />}
                      onClick={handleMergeColumns}
                    >
                      Merge
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {excelData.rows.map((row, rowIndex) => (
                <TableRow key={row.id}>
                  {row.data.map((cell, colIndex) => (
                    <TableCell
                      key={colIndex}
                      sx={getCellStyle(rowIndex, colIndex, cell)}
                      onClick={() => handleCellClick(rowIndex, colIndex)}
                    >
                      {editingCell?.rowIndex === rowIndex && editingCell?.colIndex === colIndex ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                            autoFocus
                            sx={{ flex: 1 }}
                          />
                          <Tooltip title="Save">
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditSave();
                              }} 
                              color="primary"
                            >
                              <SaveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditCancel();
                              }} 
                              color="error"
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span>{cell}</span>
                          {row.type === 'section' && colIndex === 0 && (
                            <Tooltip title="Edit">
                              <IconButton size="small" sx={{ ml: 1 }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Instructions */}
      <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          How to use this Excel-like interface:
        </Typography>
        <Typography variant="body2" component="div">
          • <strong>Section titles</strong>: Click on section names to edit them
          <br />
          • <strong>Activity names</strong>: Click on activity names to edit them
          <br />
          • <strong>Visit columns</strong>: Click on cells to toggle 'X' marks for visits
          <br />
          • <strong>Add Row</strong>: Add new sections or activities with position control
          <br />
          • <strong>Add Column</strong>: Add new visit columns with position control
          <br />
          • <strong>Delete Columns</strong>: Click the delete icon on column headers to remove columns
          <br />
          • <strong>Merge Columns</strong>: Select multiple columns and click Merge to combine them
          <br />
          • <strong>Save Changes</strong>: Click the Save button to persist your changes to the database
          <br />
          • <strong>Refresh Data</strong>: Click Refresh to reload the latest data from the database
          <br />
          • <strong>Export to Excel</strong>: Download the current data as an Excel file
          <br />
          • <strong>Data Persistence</strong>: All changes are saved to the database and will be available after page reload
        </Typography>
      </Box>


      {/* Summary */}
      {data.summary && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Summary:
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            maxRows={8}
            value={data.summary}
            variant="outlined"
            InputProps={{
              readOnly: true,
              sx: {
                backgroundColor: '#f5f5f5',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              },
            }}
          />
        </Box>
      )}

      <Snackbar open={saveSuccess} autoHideDuration={3000} onClose={() => setSaveSuccess(false)}>
        <Alert onClose={() => setSaveSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Changes saved successfully! Data has been updated in the database.
        </Alert>
      </Snackbar>
      <Snackbar open={!!saveError} autoHideDuration={4000} onClose={() => setSaveError('')}>
        <Alert onClose={() => setSaveError('')} severity="error" sx={{ width: '100%' }}>
          {saveError}
        </Alert>
      </Snackbar>

      {/* Add Column Dialog */}
      <Dialog open={addColOpen} onClose={() => setAddColOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Column</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Column Name"
              value={newColName}
              onChange={(e) => setNewColName(e.target.value)}
              variant="outlined"
              placeholder="e.g., Week 5, Day 35, Follow-up"
              autoFocus
            />
            <FormControl fullWidth>
              <InputLabel>Position</InputLabel>
              <Select
                value={colPosition}
                onChange={(e) => setColPosition(e.target.value)}
                label="Position"
              >
                <MenuItem value="beginning">At the beginning</MenuItem>
                <MenuItem value="end">At the end</MenuItem>
                <MenuItem value="after">After a column</MenuItem>
                <MenuItem value="before">Before a column</MenuItem>
              </Select>
            </FormControl>
            {(colPosition === 'after' || colPosition === 'before') && (
              <FormControl fullWidth>
                <InputLabel>Select Column</InputLabel>
                <Select
                  value={insertAfterCol}
                  onChange={(e) => setInsertAfterCol(e.target.value)}
                  label="Select Column"
                >
                  {excelData.header.slice(1).map((col, index) => (
                    <MenuItem key={index} value={index}>
                      {col}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddColOpen(false)}>Cancel</Button>
          <Button onClick={handleAddColumnConfirm} variant="contained" color="primary">
            Add Column
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Row Dialog */}
      <Dialog open={addRowOpen} onClose={() => setAddRowOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Row</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Row Type</InputLabel>
              <Select
                value={newRowType}
                onChange={(e) => setNewRowType(e.target.value)}
                label="Row Type"
              >
                <MenuItem value="section">Section (Header)</MenuItem>
                <MenuItem value="activity">Activity (Under a section)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Row Name"
              value={newRowName}
              onChange={(e) => setNewRowName(e.target.value)}
              variant="outlined"
              placeholder={newRowType === 'section' ? 'e.g., Safety Assessments' : 'e.g., Physical Examination'}
              autoFocus
            />
            <FormControl fullWidth>
              <InputLabel>Position</InputLabel>
              <Select
                value={rowPosition}
                onChange={(e) => setRowPosition(e.target.value)}
                label="Position"
              >
                <MenuItem value="beginning">At the beginning</MenuItem>
                <MenuItem value="end">At the end</MenuItem>
                <MenuItem value="after">After a row</MenuItem>
                <MenuItem value="before">Before a row</MenuItem>
              </Select>
            </FormControl>
            {(rowPosition === 'after' || rowPosition === 'before') && (
              <FormControl fullWidth>
                <InputLabel>Select Row</InputLabel>
                <Select
                  value={insertAfterRow}
                  onChange={(e) => setInsertAfterRow(e.target.value)}
                  label="Select Row"
                >
                  {excelData.rows.map((row, index) => (
                    <MenuItem key={index} value={index}>
                      {row.data[0]} ({row.type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddRowOpen(false)}>Cancel</Button>
          <Button onClick={handleAddRowConfirm} variant="contained" color="primary">
            Add Row
          </Button>
        </DialogActions>
      </Dialog>

      {/* Column Edit Dialog */}
      <Dialog 
        open={editColumnDialogOpen} 
        onClose={handleColumnDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Column Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Period (e.g., Every 3 Weeks, C1D1, Screening)"
            fullWidth
            variant="outlined"
            value={editColumnPeriod}
            onChange={(e) => setEditColumnPeriod(e.target.value)}
            placeholder="Enter period name..."
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Day (e.g., ±3 Days, Day 1, Day -28 to -1)"
            fullWidth
            variant="outlined"
            value={editColumnDay}
            onChange={(e) => setEditColumnDay(e.target.value)}
            placeholder="Enter day information..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleColumnCancel}>Cancel</Button>
          <Button 
            onClick={handleColumnSave} 
            variant="contained" 
            color="primary"
            disabled={!editColumnPeriod.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Keynotes Edit Dialog */}
      <Dialog 
        open={keynotesDialogOpen} 
        onClose={handleKeynotesDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Key Notes</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Activity: <strong>{editingKeynotesActivityName}</strong>
            </Typography>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={6}
              maxRows={12}
              label="Keynotes (Footnotes)"
              value={keynotesEditValue}
              onChange={(e) => setKeynotesEditValue(e.target.value)}
              placeholder="Enter footnote text here..."
              variant="outlined"
              sx={{ mt: 2 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              This text will appear in the Keynotes column and will be saved as a footnote reference.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleKeynotesDialogClose}>Cancel</Button>
          <Button 
            onClick={handleKeynotesDialogSave} 
            variant="contained" 
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ExcelLikeSOA; 